import Events from '../../../../../utilities/EventEmitter';
import appConstants from '../../../../../appConstants';
import {
  calculatePickupOperationMetrics,
  createDefaultPickupOperation
} from './utils';
import { storeBrowserPerformanceData } from './browserPerformanceUtils';
import { RMAXLogger } from '../../../../../services/LoggerService/RMAXLogger';

const createPickupOperationsLogger = () => {
  const pickupOperationTrackingMap = new Map();

  const handleSignalREvent = (pickupId, pickupOperationTimeTrackingId) => {
    const pickupOperationTrackingData = pickupOperationTrackingMap.get(
      pickupOperationTimeTrackingId
    );
    if (!pickupOperationTrackingData) return;
    const timestamp = Date.now();
    pickupOperationTrackingData.timestamps.signalREvents.push({
      pickupId,
      timestamp
    });
    pickupOperationTrackingData.signalREventsReceived++;
    if (
      pickupOperationTrackingData.signalREventsReceived ===
      pickupOperationTrackingData.totalSignalREventsExpected
    ) {
      pickupOperationTrackingData.timestamps.dispatchRouteApi.requestStart = Date.now();
    }
  };

  // Event listeners persist for the entire application lifecycle since this is a singleton logger
  Events.on(appConstants.PICKUP_EVENTS.ASSIGN_PICKUP, handleSignalREvent);
  Events.on(appConstants.PICKUP_EVENTS.UNASSIGN_PICKUP, handleSignalREvent);

  // Clear the entry after logging metrics also when dispatch header is unmounted or terminal changed
  const cleanupOperationTrackingData = (pickupOperationTimeTrackingId = null) => {
    pickupOperationTimeTrackingId
      ? pickupOperationTrackingMap.delete(pickupOperationTimeTrackingId)
      : pickupOperationTrackingMap.clear();
  };

  // Get operation tracking data
  const getOperationTrackingData = (pickupOperationTimeTrackingId) => {
    return pickupOperationTrackingMap.get(pickupOperationTimeTrackingId);
  };

  // Stage 1: Initialize pickup operation tracking
  const initializePickupOperationTracking = (actionType, { pickupIds, driverId }) => {
    const operation = createDefaultPickupOperation({ actionType, driverId, pickupIds });
    const pickupOperationTimeTrackingId = operation.pickupOperationTimeTrackingId;
    pickupOperationTrackingMap.set(pickupOperationTimeTrackingId, operation);
    return pickupOperationTimeTrackingId;
  };

  // Stage 2: Record initial pickup modification API response
  const recordPickupModificationApiResponse = (pickupOperationTimeTrackingId, url) => {
    const pickupOperationTrackingData = pickupOperationTrackingMap.get(
      pickupOperationTimeTrackingId
    );
    if (!pickupOperationTrackingData) return;
    pickupOperationTrackingData.timestamps.pickupOperationApi.response = Date.now();
    
    // Store browser performance data for pickup operation API
    storeBrowserPerformanceData({ 
      pickupOperationTrackingData, 
      apiType: 'pickupOperationApi', 
      url 
    });
  };

  // Stage 3a: Start tracking dispatch routes refresh deduplication
  const startDispatchRoutesRefreshTracking = (pickupOperationTimeTrackingId) => {
    const pickupOperationTrackingData = pickupOperationTrackingMap.get(
      pickupOperationTimeTrackingId
    );
    if (!pickupOperationTrackingData) return;
    pickupOperationTrackingData.timestamps.dispatchRouteApi.deduplicationStart = Date.now();
  };

  // Stage 3b: Record actual dispatch routes API call start
  const startActualDispatchRoutesApiCall = (pickupOperationTimeTrackingId) => {
    const pickupOperationTrackingData = pickupOperationTrackingMap.get(
      pickupOperationTimeTrackingId
    );
    if (!pickupOperationTrackingData) return;
    pickupOperationTrackingData.timestamps.dispatchRouteApi.requestStart = Date.now();
  };

  // Stage 3c: Complete dispatch routes refresh tracking
  const completeDispatchRoutesRefreshTracking = (pickupOperationTimeTrackingId, url) => {
    const pickupOperationTrackingData = pickupOperationTrackingMap.get(
      pickupOperationTimeTrackingId
    );
    if (!pickupOperationTrackingData) return;
    pickupOperationTrackingData.timestamps.dispatchRouteApi.response = Date.now();
    
    // Store browser performance data for dispatch routes API
    storeBrowserPerformanceData({ 
      pickupOperationTrackingData, 
      apiType: 'dispatchRouteApi', 
      url 
    });
    
    pickupOperationTrackingData.completed = true;
    calculatePickupOperationMetrics(pickupOperationTrackingData);
  };

  // Stage 5: Finalize pickup operation tracking and log metrics
  const finalizeOperationAndLogMetrics = (pickupOperationTimeTrackingId) => {
    const pickupOperationTrackingData = pickupOperationTrackingMap.get(
      pickupOperationTimeTrackingId
    );
    if (!pickupOperationTrackingData) return;
    pickupOperationTrackingData.timestamps.loaderClosed = Date.now();
    pickupOperationTrackingData.pickupOperationFinishTime = new Date();
    const metrics = calculatePickupOperationMetrics(pickupOperationTrackingData);
    const logMessage = `Pickup Operation Metrics: operationType=${pickupOperationTrackingData.actionType} driverId=${pickupOperationTrackingData.driverId}`;
    RMAXLogger.info(logMessage, metrics);
    cleanupOperationTrackingData(pickupOperationTimeTrackingId); // Clean up the operation after logging
  };

  return {
    // Get operation tracking data
    getOperationTrackingData,

    // Clean up operation tracking data
    cleanupOperationTrackingData,

    // Stage 1: Initialize pickup operation tracking
    initializePickupOperationTracking,

    // Stage 2: Record initial pickup modification API response
    recordPickupModificationApiResponse,

    // Stage 3a: Start tracking dispatch routes refresh deduplication
    startDispatchRoutesRefreshTracking,

    // Stage 3b: Record actual dispatch routes API call start
    startActualDispatchRoutesApiCall,

    // Stage 3c: Complete dispatch routes refresh tracking
    completeDispatchRoutesRefreshTracking,

    // Stage 5: Finalize pickup operation tracking and log metrics
    finalizeOperationAndLogMetrics
  };
};

// Single instance is created and shared across all imports
const pickupOperationsLogger = createPickupOperationsLogger();
export default pickupOperationsLogger;
